import * as api from '@gitbook/api-client';
import {
    Block,
    type KeyedNodeType,
    Leaf,
    Mark,
    type SchemaNormalizeFn,
    Text,
} from '@gitbook/slate';
import type { Orderable } from '@gitbook/util-orderindex';
import { assertIsDefined } from '@gitbook/util-ts';

import { createTableFromData } from '../table';
import { createTabs, createTabsItem } from '../tabs';
import {
    BLOCK_HEADING_1,
    BLOCK_HEADING_3,
    BLOCK_OPENAPI,
    BLOCK_PARAGRAPH,
    MARK_CODE,
    MARK_COLOR,
    type MarkColorData,
} from '../types';

export const FRAGMENT_SWAGGER_OPERATION_DESCRIPTION = 'swagger-operation-description';
export const FRAGMENT_SWAGGER_PARAMETER_DESCRIPTION = 'swagger-parameter-description';
export const FRAGMENT_SWAGGER_RESPONSE_DESCRIPTION = 'swagger-response-description';
export const FRAGMENTS_SWAGGER_SET = new Set<SwaggerFragment>([
    FRAGMENT_SWAGGER_OPERATION_DESCRIPTION,
    FRAGMENT_SWAGGER_PARAMETER_DESCRIPTION,
    FRAGMENT_SWAGGER_RESPONSE_DESCRIPTION,
]);
export type SwaggerFragment =
    | typeof FRAGMENT_SWAGGER_OPERATION_DESCRIPTION
    | typeof FRAGMENT_SWAGGER_PARAMETER_DESCRIPTION
    | typeof FRAGMENT_SWAGGER_RESPONSE_DESCRIPTION;

/** Definition of a parameter in a swagger block. */
export type SwaggerParameterLocation = 'query' | 'header' | 'path' | 'cookie' | 'body';
export interface SwaggerParameter extends Orderable {
    in: SwaggerParameterLocation;
    name?: string;
    type?: string;
    required?: boolean;
    // Synced parameters have a description
    description?: string;
    // Synced "object" type have properties
    properties?: SwaggerParametersMap;
    // Synced "array" type have items
    items?: SwaggerParameter;
}

export type SwaggerParametersMap = { [key: string]: SwaggerParameter };
export type SwaggerResponsesMap = { [key: string]: SwaggerResponse };
export interface SwaggerParameterKV {
    key: string;
    value: SwaggerParameter;
}
export interface SwaggerResponseKV {
    key: string;
    value: SwaggerResponse;
}

/** Definition of a response in a swagger block. */
export interface SwaggerResponse extends Orderable {
    status: string;
    description: string;
}

export interface BlockSwaggerDataFromScratch {
    baseUrl: string;
    path: string;
    method: string;
    summary: string;
    parameters?: SwaggerParametersMap;
    responses?: SwaggerResponsesMap;
    expanded?: boolean;
    fullWidth?: boolean;
}

const parameterTypes: {
    in: SwaggerParameterLocation;
    title: string;
}[] = [
    {
        in: 'path',
        title: 'Path Parameters',
    },
    {
        in: 'query',
        title: 'Query Parameters',
    },
    {
        in: 'header',
        title: 'Headers',
    },
    {
        in: 'cookie',
        title: 'Cookies',
    },
    {
        in: 'body',
        title: 'Request Body',
    },
];

const methodToColor: { [key: string]: MarkColorData['text'] } = {
    GET: api.DocumentTextColor.Blue,
    POST: api.DocumentTextColor.Green,
    PUT: api.DocumentTextColor.Orange,
    DELETE: api.DocumentTextColor.Red,
    PATCH: api.DocumentTextColor.Purple,
};

/**
 * Validation functions to replace all legacy editable API blocks.
 */
export function replaceEditableApiBlocks(node: KeyedNodeType): SchemaNormalizeFn | undefined {
    if (node.object !== 'block' || node.type !== BLOCK_OPENAPI || !!node.data.has('ref')) {
        return;
    }

    const data = node.data.toFrozenObject() as BlockSwaggerDataFromScratch;

    const blocks: Block[] = [];

    if (data.summary) {
        blocks.push(
            Block.create({
                type: BLOCK_HEADING_1,
                nodes: [
                    Text.create({
                        text: data.summary,
                    }),
                ],
            })
        );
    }

    blocks.push(
        Block.create({
            type: BLOCK_PARAGRAPH,
            nodes: [
                Text.create({
                    text: +' ' + data.path,
                    leaves: [
                        Leaf.create({
                            text: data.method.toUpperCase(),
                            marks: [
                                Mark.create({
                                    type: MARK_CODE,
                                }),
                                Mark.create({
                                    type: MARK_COLOR,
                                    data: {
                                        text: methodToColor[data.method.toUpperCase()] ?? 'default',
                                        background: 'default',
                                    } as MarkColorData,
                                }),
                            ],
                        }),
                        Leaf.create({
                            text: ' ',
                        }),
                        Leaf.create({
                            text: `${data.baseUrl}${data.path}`,
                            marks: [
                                Mark.create({
                                    type: MARK_CODE,
                                }),
                            ],
                        }),
                    ],
                }),
            ],
        })
    );

    const descriptionFragment = node.getFragmentsByType(FRAGMENT_SWAGGER_OPERATION_DESCRIPTION)[0];
    if (descriptionFragment && !descriptionFragment.isEmpty) {
        blocks.push(...descriptionFragment.nodes.toArray());
    }

    parameterTypes.forEach(({ in: location, title }) => {
        const parameters = Object.entries(data.parameters ?? {}).filter(
            (p) => p[1].in === location
        );

        if (parameters.length === 0) {
            return;
        }

        blocks.push(
            Block.create({
                type: BLOCK_HEADING_3,
                nodes: [
                    Text.create({
                        text: title,
                    }),
                ],
            })
        );
        blocks.push(getBlockFromParameters(node, parameters));
    });

    const responseEntries = Object.entries(data.responses ?? {});
    if (responseEntries.length > 0) {
        const tabEntries = responseEntries.map(([fragmentKey, response]) => {
            const fragment = node.getFragmentByKey(fragmentKey);

            return createTabsItem(
                `${response.status} ${response.description}`,
                fragment?.nodes.toArray() ?? []
            );
        });

        if (tabEntries.length > 0) {
            blocks.push(createTabs(tabEntries));
        }
    }

    return (change) => {
        const parent = change.value.document.getParent(node.key);
        assertIsDefined(parent);
        const index = parent.getChildIndex(node.key);

        change.removeNodeByKey(node.key, { normalize: false });

        blocks.forEach((block, blockIndex) => {
            change.insertNodeByKey(parent.key, index + blockIndex, block, { normalize: false });
        });
    };
}

function getBlockFromParameters(node: Block, parameters: [string, SwaggerParameter][]): Block {
    return createTableFromData(
        [
            {
                title: 'Name',
            },
            {
                title: 'Type',
            },
            {
                title: 'Description',
            },
        ],
        parameters.map(([key, parameter]) => {
            const leaves: Leaf[] = [
                Leaf.create({
                    text: parameter.name ?? '',
                }),
            ];
            if (parameter.required) {
                leaves.push(
                    Leaf.create({
                        text: '*',
                        marks: [
                            Mark.create({
                                type: MARK_COLOR,
                                data: {
                                    text: 'red',
                                    background: 'default',
                                } as MarkColorData,
                            }),
                        ],
                    })
                );
            }
            return [
                {
                    value: [
                        Block.create({ type: BLOCK_PARAGRAPH, nodes: [Text.create({ leaves })] }),
                    ],
                },
                {
                    value: parameter.type ?? 'String',
                },
                {
                    value: node.getFragmentByKey(key) ?? '',
                },
            ];
        })
    );
}
